import { HistoryOutlined, LeftCircleOutlined, RightCircleOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { Components } from '@fasstech/flexiget';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { compose, equals, isNil, path, pick, prop, propOr, reject, sum, values as RValues, includes, propEq, isNotNil } from 'ramda';
import { Breadcrumb } from '../../../../components';
import { DocumentProvider, useDocumentContext } from '../../../../context/Document';
import { BASE_PADDING_DOCUMENT, SCOPE_OFFER } from '../../../../_CONST';
import Loading from '../../../../components/Loading';
import useFetchQuery from '../../../../hooks/useFetchQuery';
import { QCommentsQuery } from '../../../../_graphql/queries/comments/QComments';
import { QGlobalVariablesQuery } from '../../../../_graphql/queries/documents/QGlobalVariables';
import { QPredefinedVariablesQuery } from '../../../../_graphql/queries/QPredefinedVariables';
import { QWidgetsQuery } from '../../../../_graphql/queries/documents/QWidgets';
import { QOfferQuery } from '../../../../_graphql/queries/offers/QOffer';
import GlobalVariableForm from '../globalVariable/GlobalVariableForm';
import withUser from '../../../../withUser';
import CreateWidgetModal from './CreateWidgetModal';
import PreviewDocument from './PreviewDocument';
import EditWidgetModal from './EditWidgetModal';
import DocumentFormTabs from './DocumentFormTabs';
import CssVariables from './CssVariables';
import { getHighlightNavigationTarget } from './helpers/highlightNavigation';
import { scrollToCard } from './helpers/scrollToCard';

export const DocumentForm = ({
  commentsMode,
  breadcrumb
}) => {
  const {
    values,
    previewHtmlLoading,
    previewHtml,
    documentPreviewUrl,
    previewDocumentHtml,
    isEdit,
    widgetIdEditing,
    rowIndexEditing,
    document,
    offerId,
    documentId,
    classNameOrientationDocument,
    pageHeightDocument,
    comments,
    widgets,
    isOpenCreateWidgetModal,
    isOpenCreateGlobalVariableModal,
    setIsOpenCreateGlobalVariableModal,
    setGlobalVariableCreated,
    highlightedWidgetId,
    setHighlightedWidgetId
  } = useDocumentContext();

  const [showSettings, setShowSettings] = useState(true);
  const [activeKey, setActiveKey] = useState('0');
  const [activeMainTabKey, setActiveMainTabKey] = useState('1');
  const [pendingScrollCardColId, setPendingScrollCardColId] = useState(null);

  const history = useHistory();

  const { offer } = useFetchQuery({
    query: QOfferQuery,
    args: { offerId },
    dataProp: 'offer'
  });

  const docIsNotEditable = useMemo(() => {
    const isRawDocument = propEq('rawDocument', 'type')(document);
    const offerStatus = path(['version', 'status'], offer);
    return includes(offerStatus, ['review', 'release']) && !isRawDocument;
  }, [document, offer]);

  const displaySettings = !docIsNotEditable && showSettings;

  useEffect(() => {
    if (docIsNotEditable && !previewHtmlLoading) {
      previewDocumentHtml();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docIsNotEditable]);

  // Effect 1 : when a widget is clicked in the preview, switch section tab and store scroll intent
  useEffect(() => {
    if (!highlightedWidgetId) return;

    const { position } = highlightedWidgetId;
    const { sectionIndex, cardColId, mainTabKey } = getHighlightNavigationTarget(position);

    setPendingScrollCardColId(cardColId);
    setActiveKey(sectionIndex);
    // Force the "Création" tab (the one containing the widgets panel) to be displayed:
    // otherwise, if the user is on the "Commentaires"/"Variables" tab, the highlight
    // is applied to a hidden panel (display:none) and is never visible.
    setActiveMainTabKey(mainTabKey);
   
  }, [highlightedWidgetId]);

  // Effect 2 : once pendingScrollCardColId is set and DOM is ready, scroll and highlight
  // Holds the id of the pending "remove highlight" timeout across renders. A ref (not a
  // plain closure variable) is required here: the setState calls below are not batched
  // outside of React event handlers, so they can synchronously trigger this effect's
  // cleanup *before* the setTimeout call even runs, making a closure variable unreliable
  // for cleanup. The ref always reflects the latest scheduled timeout, whenever it runs.
  const highlightTimeoutRef = useRef(null);

  useEffect(() => {
    if (!pendingScrollCardColId) return;

    // Use requestAnimationFrame to ensure the DOM has painted after activeKey change
    const rafId = requestAnimationFrame(() => {
      const el = window.document.getElementById(pendingScrollCardColId);
      if (!el) return;

      // No `el.scrollIntoView()` here: it would also scroll the `overflow: hidden`
      // ancestors, which the user cannot scroll back (see the scrollToCard header
      // comment).
      scrollToCard(el);
      el.classList.add('widget-card-highlighted');
      setPendingScrollCardColId(null);
      setHighlightedWidgetId(null);

      // Clear any still-pending timeout from a previous highlight before overwriting the
      // ref: otherwise, clicking a second widget before the first highlight's 3s elapse
      // would leak the first timeout (it stays scheduled, removing a class on an
      // unrelated element 3s after the first click).
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = setTimeout(() => {
        el.classList.remove('widget-card-highlighted');
      }, 3000);
    });

    return () => cancelAnimationFrame(rafId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingScrollCardColId]);

  // Clears whatever highlight-removal timeout is currently pending when the component
  // unmounts, independently of the effect above (see comment on highlightTimeoutRef).
  useEffect(() => () => clearTimeout(highlightTimeoutRef.current), []);

  const dataItemPrefix = reject(isNil, [offerId, documentId]);

  const calculatePaddingX = compose(
    sum,
    RValues,
    pick(['left', 'right']),
    propOr(BASE_PADDING_DOCUMENT, 'padding')
  );

  const actualSection = path(['pages', activeKey], values);
  const paddingXDocument = calculatePaddingX(values);
  const paddingXActualSection = calculatePaddingX(actualSection);
  const paddingXActualRow = calculatePaddingX(path(rowIndexEditing, actualSection));
  const totalPaddingX = sum([paddingXDocument, paddingXActualSection, paddingXActualRow]);

  const settingsClassNames = classNames('relative transition-all duration-300 ease-in-out opacity-100 overflow-visible', {
    'w-full col-span-3': showSettings,
    'w-4 bg-white shadow-lg': !showSettings
  });
  const settingsImgClassNames = 'bg-white rounded-full cursor-pointer text-[22px] !text-flexi-primary-color absolute left-0 top-0';

  return (
    <>
      {widgetIdEditing && <EditWidgetModal totalPaddingX={totalPaddingX} />}
      {isOpenCreateWidgetModal && <CreateWidgetModal totalPaddingX={totalPaddingX} />}
      {isNotNil(isOpenCreateGlobalVariableModal) && (
        <GlobalVariableForm
          isOpenCreateGlobalVariableModal={isOpenCreateGlobalVariableModal}
          setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
          scope={SCOPE_OFFER}
          offerId={offerId}
          modalMode
          setGlobalVariableCreated={setGlobalVariableCreated}
        />
      )}
      <div className={`${displaySettings ? 'grid md:grid-cols-7 grid-cols-1' : 'flex h-full'}`}>
        <div className={displaySettings ? 'col-span-4' : 'w-full'}>
          <div className="mt-4 ml-10">
            <Breadcrumb value={breadcrumb} />

            {isEdit && (
              <Button
                size="small"
                type="primary"
                onClick={() => history.push(`/offre/${offerId}/documents/${prop('id', document)}/historique`)}
                icon={<HistoryOutlined />}
              >
                Historique du document
              </Button>
            )}

            <div className="font-bold text-xl uppercase my-6 flex items-center">
              <img
                alt="Retour à l'édition de l'offre"
                src="/icons/icon-fleche.svg"
                className="cursor-pointer mr-3 h-4"
                onClick={() => history.push(`/offre/${offerId}/edition`)}
              />
              {isEdit ? 'Modifier le document' : 'Créer un document'}
            </div>
          </div>

          <PreviewDocument
            documentPreviewUrl={documentPreviewUrl}
            pagesHtml={previewHtml}
            previewDocumentHtml={previewDocumentHtml}
            previewHtmlLoading={previewHtmlLoading}
            classNameOrientationDocument={classNameOrientationDocument}
            pageHeightDocument={pageHeightDocument}
            activeKey={activeKey}
            onWidgetHighlight={(widgetId, position) => {
              setShowSettings(true);
              setHighlightedWidgetId({ widgetId, position });
            }}
          />
        </div>

        {!docIsNotEditable && (
          <div className={settingsClassNames}>
            <Components.Tooltip
              title={showSettings ? 'Réduire' : 'Agrandir'}
              placement="left"
              className="absolute -left-2.5 top-10"
            >
              {showSettings ? (
                <RightCircleOutlined
                  className={settingsImgClassNames}
                  onClick={() => setShowSettings(!showSettings)}
                />
              ) : (
                <LeftCircleOutlined
                  className={settingsImgClassNames}
                  onClick={() => setShowSettings(!showSettings)}
                />
              )}
            </Components.Tooltip>
            <DocumentFormTabs
              dataItemPrefix={dataItemPrefix}
              comments={comments}
              showSettings={showSettings}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              activeMainTabKey={activeMainTabKey}
              setActiveMainTabKey={setActiveMainTabKey}
              widgets={widgets}
            />
          </div>
        )}
      </div>
    </>
  );
};

const DocumentFormProvider = ({
  document,
  scope,
  offerId,
  commentsMode,
  breadcrumb,
  cacheKey,
  userTeam
}) => {
  const { comments, isLoading: isLoadingComments } = useFetchQuery({
    query: QCommentsQuery,
    args: { itemRootId: propOr('', 'id', document) },
    dataProp: 'comments',
    defaultData: { id: 'comments-null', comments: [] }
  });

  const { globalVariables, isLoading: isLoadingGlobalVariables } = useFetchQuery({
    query: QGlobalVariablesQuery,
    args: {
      scope,
      offerId,
      checkUsage: false
    },
    dataProp: 'globalVariables',
    defaultData: []
  });

  const { predefinedVariables, isLoading: isLoadingPredefinedVariables } = useFetchQuery({
    query: QPredefinedVariablesQuery,
    args: {},
    dataProp: 'predefinedVariables',
    defaultData: []
  });

  const { widgets: widgetsFetch, isLoading: isLoadingWidgets, reFetch } = useFetchQuery({
    query: QWidgetsQuery,
    args: {
      scope: equals(scope, SCOPE_OFFER) ? undefined : scope,
      offerId
    },
    dataProp: 'widgets',
    defaultData: [],
    cacheKey
  });

  if (isLoadingWidgets || isLoadingComments || isLoadingGlobalVariables || isLoadingPredefinedVariables) return <Loading/>;

  return (
    <DocumentProvider
      document={document}
      options={{
        scope,
        offerId
      }}
      comments={comments}
      globalVariablesFetch={globalVariables}
      predefinedVariables={predefinedVariables}
      widgetsFetch={widgetsFetch}
      setRefetch={reFetch}
      userTeam={userTeam}
    >
      <CssVariables />
      <DocumentForm
        commentsMode={commentsMode}
        breadcrumb={breadcrumb}
      />
    </DocumentProvider>
  );
};

export default withUser(DocumentFormProvider);
